/**
 * Canvas map renderer. Everything outside the party's light is black; remembered squares are
 * drawn faintly; dim light is visibly murky. The camera follows your character smoothly (tokens
 * glide from square to square at walking pace), unless you drag it away with the right mouse button.
 */
import type { CreatureView, DungeonView, PlayerView } from '../shared/view.ts';
import type { Effects } from './fx.ts';
import { EMOJI_FONT, factionColor, sortedStatus, statusStyle } from './status.ts';
import { doorRun, doorSquares } from './doors.ts';
import { doorSlice, TEXTURE_SIZE, TileTextures, wallMask } from './textures.ts';
import { tokenImage } from './tokenizer.ts';

export interface Overlay {
  reachable: Set<number>;
  targets: Set<string>;
  targetTiles: Set<number>;
  /** Squares an area spell would hit at the hovered aim point. */
  areaTiles: Set<number>;
  hover: { x: number; y: number } | null;
  /** Whose turn it is: a ring on each (the party's characters take theirs together). */
  currentIds: ReadonlySet<string>;
  /** Squares the open loot panel lists, and the one that was clicked. */
  lootTiles: Set<number>;
  lootFocus: number | null;
  /** The creatures you're talking to: one, or a band (their tokens get a ring). */
  talkTargets: ReadonlySet<string>;
  /** The token you act with while it's your turn: it glows and pulses. */
  turnGlow: string | null;
  /** What creatures just said, over their tokens (age and how long it shows, in ms). */
  bubbles: { id: string; text: string; age: number; ms: number }[];
}

const HEALTH_RATIO: Record<CreatureView['health'], number> = {
  unhurt: 1, scratched: 0.85, wounded: 0.65, bloodied: 0.4, 'near death': 0.15, down: 0, dead: 0,
};

export const MIN_TILE = 20;
export const MAX_TILE = 72;

/** A stable pseudo-random number per square, so rubble and spores don't shimmer. */
function hash(x: number, y: number, n: number): number {
  const v = Math.sin(x * 127.1 + y * 311.7 + n * 74.7) * 43758.5453;
  return v - Math.floor(v);
}

/**
 * Hazardous terrain, drawn over the floor before lighting shades it. `same` says whether a
 * neighbouring square holds the same terrain, so a rubble pile reads as one mass.
 */
function drawTerrain(g: CanvasRenderingContext2D, code: string, sx: number, sy: number, T: number, x: number, y: number, time: number, same: (dx: number, dy: number) => boolean): void {
  const cx = sx + T / 2, cy = sy + T / 2;
  switch (code) {
    case '^': {
      g.fillStyle = '#050403';
      g.fillRect(sx + 2, sy + 2, T - 4, T - 4);
      const grad = g.createLinearGradient(sx, sy, sx, sy + T);
      grad.addColorStop(0, 'rgba(120,90,60,0.55)');
      grad.addColorStop(0.25, 'rgba(0,0,0,0)');
      g.fillStyle = grad;
      g.fillRect(sx + 2, sy + 2, T - 4, T - 4);
      break;
    }
    case '~': {
      g.fillStyle = '#16324a';
      g.fillRect(sx, sy, T, T);
      g.strokeStyle = 'rgba(140,200,255,0.35)';
      g.lineWidth = 1;
      for (let k = 0; k < 2; k++) {
        g.beginPath();
        const base = sy + T * (0.35 + k * 0.32);
        for (let i = 0; i <= 8; i++) {
          const px = sx + (i / 8) * T;
          const py = base + Math.sin(time / 500 + i * 0.9 + x * 1.3 + k) * T * 0.05;
          if (i === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.stroke();
      }
      break;
    }
    case '=': {
      g.fillStyle = 'rgba(170,220,255,0.16)';
      g.fillRect(sx, sy, T, T);
      g.strokeStyle = 'rgba(210,240,255,0.28)';
      g.lineWidth = 1;
      for (let k = -1; k <= 1; k++) {
        g.beginPath();
        g.moveTo(sx + T * (0.2 + k * 0.35), sy + T * 0.85);
        g.lineTo(sx + T * (0.55 + k * 0.35), sy + T * 0.15);
        g.stroke();
      }
      break;
    }
    case '*': {
      const pulse = 0.55 + 0.45 * Math.sin(time / 380 + x + y);
      g.strokeStyle = `rgba(190,140,255,${0.5 + pulse * 0.4})`;
      g.lineWidth = Math.max(1, T / 22);
      g.beginPath();
      g.arc(cx, cy, T * 0.3, 0, Math.PI * 2);
      g.stroke();
      g.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = -Math.PI / 2 + (i * 4 * Math.PI) / 5;
        const px = cx + Math.cos(a) * T * 0.26, py = cy + Math.sin(a) * T * 0.26;
        if (i === 0) g.moveTo(px, py);
        else g.lineTo(px, py);
      }
      g.closePath();
      g.stroke();
      break;
    }
    case '%': {
      // Inset by a pixel only where the pile ends.
      const l = same(-1, 0) ? 0 : 1, t = same(0, -1) ? 0 : 1, r = same(1, 0) ? 0 : 1, b = same(0, 1) ? 0 : 1;
      g.fillStyle = '#4a433b';
      g.fillRect(sx + l, sy + t, T - l - r, T - t - b);
      for (let i = 0; i < 6; i++) {
        const px = sx + T * (0.15 + hash(x, y, i) * 0.7), py = sy + T * (0.15 + hash(x, y, i + 9) * 0.7);
        const r = T * (0.1 + hash(x, y, i + 17) * 0.12);
        g.fillStyle = i % 2 ? '#7a7065' : '#5f574e';
        g.beginPath();
        g.moveTo(px - r, py);
        g.lineTo(px - r * 0.3, py - r);
        g.lineTo(px + r, py - r * 0.4);
        g.lineTo(px + r * 0.6, py + r * 0.8);
        g.closePath();
        g.fill();
      }
      break;
    }
    case 'e': {
      // Heaped earth: a mound shaded from its crest, ridged where the ramparts run on.
      const l = same(-1, 0) ? 0 : 2, r = same(1, 0) ? 0 : 2;
      const grad = g.createLinearGradient(sx, sy, sx, sy + T);
      grad.addColorStop(0, 'rgba(120,92,58,0.9)');
      grad.addColorStop(0.5, 'rgba(84,62,38,0.9)');
      grad.addColorStop(1, 'rgba(50,36,22,0.9)');
      g.fillStyle = grad;
      g.fillRect(sx + l, sy + 2, T - l - r, T - 4);
      g.strokeStyle = 'rgba(160,128,84,0.7)';
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(sx + l, sy + T * 0.35); g.lineTo(sx + T - r, sy + T * 0.35);
      g.moveTo(sx + l, sy + T * 0.7); g.lineTo(sx + T - r, sy + T * 0.7);
      g.stroke();
      break;
    }
    case 'w': {
      g.strokeStyle = 'rgba(235,235,235,0.55)';
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(sx, sy); g.lineTo(sx + T, sy + T);
      g.moveTo(sx + T, sy); g.lineTo(sx, sy + T);
      g.moveTo(cx, sy); g.lineTo(cx, sy + T);
      g.moveTo(sx, cy); g.lineTo(sx + T, cy);
      g.stroke();
      g.beginPath();
      g.arc(cx, cy, T * 0.18, 0, Math.PI * 2);
      g.arc(cx, cy, T * 0.34, 0, Math.PI * 2);
      g.stroke();
      break;
    }
    case '"':
    case ',': {
      for (let i = 0; i < 7; i++) {
        const px = sx + T * (0.12 + hash(x, y, i) * 0.76), py = sy + T * (0.12 + hash(x, y, i + 5) * 0.76);
        g.fillStyle = code === '"' ? 'rgba(170,220,110,0.8)' : 'rgba(170,220,110,0.3)';
        g.beginPath();
        g.arc(px, py, T * (0.04 + hash(x, y, i + 3) * 0.05), 0, Math.PI * 2);
        g.fill();
      }
      break;
    }
    case 'f': {
      // A freeze ray's frost: a pale sheet of ice with a few cracks.
      g.fillStyle = 'rgba(190,230,255,0.28)';
      g.fillRect(sx, sy, T, T);
      g.strokeStyle = 'rgba(235,250,255,0.6)';
      g.lineWidth = 1;
      g.beginPath();
      for (let i = 0; i < 3; i++) {
        const px = sx + T * (0.15 + hash(x, y, i) * 0.7), py = sy + T * (0.15 + hash(x, y, i + 4) * 0.7);
        g.moveTo(px, py);
        g.lineTo(px + T * (hash(x, y, i + 8) - 0.5) * 0.4, py + T * (hash(x, y, i + 12) - 0.5) * 0.4);
      }
      g.stroke();
      break;
    }
    case 'm': {
      // A smog cloud: drifting grey-brown puffs.
      for (let i = 0; i < 4; i++) {
        const drift = Math.sin(time / 900 + i + x * 0.7 + y * 0.4) * T * 0.06;
        const px = sx + T * (0.2 + hash(x, y, i) * 0.6) + drift, py = sy + T * (0.2 + hash(x, y, i + 6) * 0.6);
        g.fillStyle = 'rgba(120,112,96,0.35)';
        g.beginPath();
        g.arc(px, py, T * (0.18 + hash(x, y, i + 2) * 0.12), 0, Math.PI * 2);
        g.fill();
      }
      break;
    }
    case 'v': {
      // Organic growth: fleshy tendrils and knots over the floor.
      g.strokeStyle = 'rgba(150,190,90,0.7)';
      g.lineWidth = Math.max(1, T / 20);
      g.beginPath();
      for (let i = 0; i < 3; i++) {
        const px = sx + T * (0.1 + hash(x, y, i) * 0.8), py = sy + T * (0.15 + hash(x, y, i + 3) * 0.7);
        g.moveTo(px, py);
        g.quadraticCurveTo(cx, cy, sx + T * (0.1 + hash(x, y, i + 6) * 0.8), sy + T * (0.15 + hash(x, y, i + 9) * 0.7));
      }
      g.stroke();
      g.fillStyle = 'rgba(190,120,140,0.7)';
      g.beginPath();
      g.arc(sx + T * (0.3 + hash(x, y, 20) * 0.4), sy + T * (0.3 + hash(x, y, 21) * 0.4), T * 0.08, 0, Math.PI * 2);
      g.fill();
      break;
    }
    case 't': {
      // Toxic fumes: a sickly green haze, rising.
      const rise = (time / 1200 + hash(x, y, 1)) % 1;
      const grad = g.createRadialGradient(cx, cy - T * 0.2 * rise, T * 0.05, cx, cy, T * 0.5);
      grad.addColorStop(0, 'rgba(160,210,80,0.45)');
      grad.addColorStop(1, 'rgba(160,210,80,0)');
      g.fillStyle = grad;
      g.fillRect(sx, sy, T, T);
      break;
    }
    case 'o': {
      // Marble mines: glassy marbles with a glint of fuse.
      for (let i = 0; i < 5; i++) {
        const px = sx + T * (0.15 + hash(x, y, i) * 0.7), py = sy + T * (0.15 + hash(x, y, i + 7) * 0.7);
        g.fillStyle = i % 2 ? 'rgba(120,170,230,0.9)' : 'rgba(230,140,90,0.9)';
        g.beginPath();
        g.arc(px, py, T * 0.07, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = 'rgba(255,255,255,0.8)';
        g.fillRect(px - T * 0.03, py - T * 0.03, Math.max(1, T * 0.025), Math.max(1, T * 0.025));
      }
      break;
    }
    case 'L': {
      const w = T * 0.32, h = T * 0.26;
      g.strokeStyle = '#f5c542';
      g.lineWidth = Math.max(1.5, T / 18);
      g.beginPath();
      g.arc(cx, cy - h * 0.2, w * 0.38, Math.PI, 0);
      g.stroke();
      g.fillStyle = '#f5c542';
      g.fillRect(cx - w / 2, cy - h * 0.2, w, h);
      break;
    }
  }
}

interface Glide {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  start: number;
  dur: number;
  /** When the last move arrived (moves set the glide's pace). */
  last: number;
  seen?: number;
}

function glidePos(gl: Glide, time: number): { x: number; y: number } {
  const t = Math.min(1, Math.max(0, (time - gl.start) / gl.dur));
  return { x: gl.fromX + (gl.toX - gl.fromX) * t, y: gl.fromY + (gl.toY - gl.fromY) * t };
}

export class MapRenderer {
  readonly canvas: HTMLCanvasElement;
  private readonly g: CanvasRenderingContext2D;
  /** Pixels per square. Closer by default; mouse wheel / +/- adjust it. */
  tile = 40;
  /** Pixels on the left covered by the loot panel when it would hide you (a narrow map): the camera centres you in the rest. */
  insetLeft = 0;
  private cam = { x: 0, y: 0 };
  /** The camera in whole screen pixels for this frame, so the map never shimmers between pixels. */
  private camPx = { x: 0, y: 0 };
  private camInit = false;
  /** Dragged away with the right mouse button: the camera stays put until it's recentered. */
  private detached = false;
  private depth = -1;
  private lastFrame = 0;
  /** Each token's glide from its last square to its new one. */
  private readonly glides = new Map<string, Glide>();
  private readonly tex = new TileTextures();

  constructor(canvas: HTMLCanvasElement, tile = 40) {
    this.canvas = canvas;
    this.g = canvas.getContext('2d')!;
    this.tile = Math.max(MIN_TILE, Math.min(MAX_TILE, tile));
  }

  /** Zoom by a step, keeping the camera centred on the same spot. */
  zoom(step: number): number {
    const next = Math.max(MIN_TILE, Math.min(MAX_TILE, this.tile + step));
    if (next === this.tile) return next;
    const cx = this.cam.x + this.cssWidth / this.tile / 2, cy = this.cam.y + this.cssHeight / this.tile / 2;
    this.tile = next;
    this.cam = { x: cx - this.cssWidth / next / 2, y: cy - this.cssHeight / next / 2 };
    return next;
  }

  private get cssWidth(): number {
    return this.canvas.clientWidth;
  }

  private get cssHeight(): number {
    return this.canvas.clientHeight;
  }

  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.floor(this.cssWidth * dpr));
    const h = Math.max(1, Math.floor(this.cssHeight * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.g.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  screenToTile(px: number, py: number): { x: number; y: number } {
    return { x: Math.floor((px + this.camPx.x) / this.tile), y: Math.floor((py + this.camPx.y) / this.tile) };
  }

  tileToScreen(x: number, y: number): { x: number; y: number } {
    return { x: x * this.tile - this.camPx.x, y: y * this.tile - this.camPx.y };
  }

  /** Is the camera following your character (rather than left where you dragged it)? */
  get following(): boolean {
    return !this.detached;
  }

  /** Drags the view by screen pixels (right mouse button): the camera stops following you. */
  pan(dx: number, dy: number): void {
    this.detached = true;
    this.cam.x -= dx / this.tile;
    this.cam.y -= dy / this.tile;
  }

  /** Follows your character again: the camera glides back to it. */
  recenter(): void {
    this.detached = false;
  }

  /**
   * Where a token is shown this frame. A one- or two-square move glides there at the pace moves
   * have been arriving (a walker's cadence), starting from wherever the token is shown now, so a
   * walk reads as one continuous motion; anything farther (a Stride, a new floor) cuts straight there.
   */
  private glide(c: CreatureView, time: number): { x: number; y: number } {
    let gl = this.glides.get(c.id);
    if (!gl) {
      gl = { fromX: c.x, fromY: c.y, toX: c.x, toY: c.y, start: time, dur: 1, last: time - 1000 };
      this.glides.set(c.id, gl);
    }
    if (gl.toX !== c.x || gl.toY !== c.y) {
      const now = glidePos(gl, time);
      const jump = Math.max(Math.abs(c.x - now.x), Math.abs(c.y - now.y));
      const since = time - gl.last;
      gl.last = time;
      if (jump > 2.5) Object.assign(gl, { fromX: c.x, fromY: c.y, toX: c.x, toY: c.y, start: time, dur: 1 });
      else Object.assign(gl, { fromX: now.x, fromY: now.y, toX: c.x, toY: c.y, start: time, dur: Math.max(70, Math.min(since, 220)) });
    }
    gl.seen = time;
    return glidePos(gl, time);
  }

  /** The camera eases toward your token with a fixed time constant, so it moves the same at any frame rate. */
  private follow(d: DungeonView, time: number, dt: number): void {
    const you = d.creatures.find((c) => c.isYou) ?? d.creatures.find((c) => c.isPc);
    if (!you) return;
    const at = this.glide(you, time);
    const half = (you.size ?? 1) / 2;
    const tx = at.x + half - (this.cssWidth + Math.min(this.insetLeft, this.cssWidth * 0.6)) / this.tile / 2;
    const ty = at.y + half - this.cssHeight / this.tile / 2;
    if (!this.camInit || (!this.detached && (Math.abs(tx - this.cam.x) > 40 || Math.abs(ty - this.cam.y) > 40))) {
      this.cam = { x: tx, y: ty };
      this.camInit = true;
      return;
    }
    if (this.detached) return;
    const k = 1 - Math.exp(-dt / 0.18);
    this.cam.x += (tx - this.cam.x) * k;
    this.cam.y += (ty - this.cam.y) * k;
  }

  draw(view: PlayerView, overlay: Overlay, time: number, fx?: Effects): void {
    this.resize();
    const g = this.g;
    const W = this.cssWidth, H = this.cssHeight;
    g.fillStyle = '#000';
    g.fillRect(0, 0, W, H);
    const d = view.dungeon;
    if (!d) return;
    // A new floor: start over, following you.
    if (d.depth !== this.depth) {
      this.depth = d.depth;
      this.detached = false;
      this.camInit = false;
      this.glides.clear();
    }
    const dt = this.lastFrame ? Math.min(0.1, Math.max(0, (time - this.lastFrame) / 1000)) : 0;
    this.lastFrame = time;
    this.follow(d, time, dt);
    const T = this.tile;
    this.camPx = { x: Math.round(this.cam.x * T), y: Math.round(this.cam.y * T) };
    // Where each token is shown (gliding between squares), and how far that is from its square.
    const shown = new Map<string, { x: number; y: number }>();
    for (const c of d.creatures) shown.set(c.id, this.glide(c, time));
    for (const [id, gl] of this.glides) if (time - (gl.seen ?? time) > 2000) this.glides.delete(id);
    const glideAt = new Map<string, { dx: number; dy: number }>();
    for (const c of d.creatures) {
      const p = shown.get(c.id)!;
      if (p.x !== c.x || p.y !== c.y) glideAt.set(`${c.x},${c.y}`, { dx: p.x - c.x, dy: p.y - c.y });
    }
    const x0 = Math.max(0, Math.floor(this.cam.x)), y0 = Math.max(0, Math.floor(this.cam.y));
    const x1 = Math.min(d.width - 1, Math.ceil(this.cam.x + W / T)), y1 = Math.min(d.height - 1, Math.ceil(this.cam.y + H / T));
    const visAt = (x: number, y: number) => d.vis[y * d.width + x];
    const tileAt = (x: number, y: number) => (x < 0 || y < 0 || x >= d.width || y >= d.height ? ' ' : d.tiles[y * d.width + x]);
    const isWall = (x: number, y: number) => tileAt(x, y) === '#';
    const terrainAt = (x: number, y: number) => (x < 0 || y < 0 || x >= d.width || y >= d.height ? ' ' : d.terrain[y * d.width + x]);
    g.textAlign = 'center';
    g.textBaseline = 'middle';

    // Tiles: textured stone, walls with a face where the floor runs below them, plank doors, and a
    // soft shadow along the walls. Snapped to whole pixels so neighbouring squares meet cleanly.
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const i = y * d.width + x;
        const t = d.tiles[i];
        if (t === ' ') continue;
        const s = this.tileToScreen(x, y);
        const px = Math.floor(s.x), py = Math.floor(s.y);
        const pw = Math.floor(s.x + T) - px, ph = Math.floor(s.y + T) - py;
        if (t === '#') {
          const below = tileAt(x, y + 1);
          g.drawImage(this.tex.wall(x, y, below !== '#' && below !== ' '), px, py, pw, ph);
        } else {
          g.drawImage(this.tex.floor(x, y), px, py, pw, ph);
          const shadow = this.tex.shadow(wallMask((dx, dy) => isWall(x + dx, y + dy)));
          if (shadow) g.drawImage(shadow, px, py, pw, ph);
          const run = doorRun(d, x, y);
          if (run) {
            // One slice of the whole double door; its padlock (if locked) is part of the strip.
            const locked = doorSquares(d.width, x, y, run).some((j) => d.terrain[j] === 'L');
            const [sx, sy] = doorSlice(run.vertical, run.index);
            g.drawImage(this.tex.door(t === "'", run.vertical, run.length, locked), sx, sy, TEXTURE_SIZE, TEXTURE_SIZE, px, py, pw, ph);
          }
        }
        const hazard = d.terrain[i];
        if (hazard && hazard !== ' ' && !(hazard === 'L' && (t === '+' || t === "'"))) {
          drawTerrain(g, hazard, s.x, s.y, T, x, y, time, (dx, dy) => terrainAt(x + dx, y + dy) === hazard);
        }
        const v = visAt(x, y);
        const shade = v === '2' ? 0 : v === '1' ? 0.42 : v === '3' ? 0.7 : 0.82;
        if (shade) {
          g.fillStyle = `rgba(0,0,0,${shade})`;
          g.fillRect(s.x, s.y, T, T);
        }
        if (v === '2') {
          g.fillStyle = 'rgba(255,160,70,0.07)';
          g.fillRect(s.x, s.y, T, T);
        }
        if (overlay.reachable.has(i)) {
          g.fillStyle = 'rgba(90,160,255,0.2)';
          g.fillRect(s.x + 1, s.y + 1, T - 2, T - 2);
        }
        if (overlay.areaTiles.has(i)) {
          g.fillStyle = 'rgba(255,110,40,0.32)';
          g.fillRect(s.x, s.y, T, T);
        }
        if (overlay.targetTiles.has(i)) {
          g.strokeStyle = 'rgba(255,200,80,0.7)';
          g.lineWidth = 1.5;
          g.strokeRect(s.x + 2, s.y + 2, T - 4, T - 4);
        }
        if (overlay.lootTiles.has(i)) {
          g.strokeStyle = overlay.lootFocus === i ? 'rgba(255,214,120,0.95)' : 'rgba(255,190,90,0.55)';
          g.lineWidth = overlay.lootFocus === i ? 2 : 1.5;
          g.setLineDash([T * 0.16, T * 0.1]);
          g.strokeRect(s.x + 2.5, s.y + 2.5, T - 5, T - 5);
          g.setLineDash([]);
        }
      }
    }

    // Hazards the party has noticed: the squares it strikes faintly hatched, the squares that set it off
    // tinted, and a warning mark where it stands (violet for a magical rune).
    for (const h of d.hazards ?? []) {
      for (const i of h.reach ?? []) {
        const s = this.tileToScreen(i % d.width, Math.floor(i / d.width));
        g.fillStyle = 'rgba(220,60,40,0.07)';
        g.fillRect(s.x, s.y, T, T);
        g.strokeStyle = 'rgba(220,60,40,0.22)';
        g.lineWidth = 1;
        g.setLineDash([T * 0.12, T * 0.12]);
        g.beginPath();
        g.moveTo(s.x + 2, s.y + T - 2);
        g.lineTo(s.x + T - 2, s.y + 2);
        g.stroke();
        g.setLineDash([]);
      }
      for (const i of h.area) {
        const s = this.tileToScreen(i % d.width, Math.floor(i / d.width));
        g.fillStyle = 'rgba(220,60,40,0.16)';
        g.fillRect(s.x, s.y, T, T);
      }
      const s = this.tileToScreen(h.x, h.y);
      g.strokeStyle = h.live ? (h.magical ? 'rgba(190,140,255,0.9)' : 'rgba(235,80,50,0.9)') : 'rgba(150,140,130,0.6)';
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(s.x + T / 2, s.y + T * 0.18);
      g.lineTo(s.x + T * 0.84, s.y + T * 0.8);
      g.lineTo(s.x + T * 0.16, s.y + T * 0.8);
      g.closePath();
      g.stroke();
      g.fillStyle = g.strokeStyle;
      g.font = `bold ${Math.round(T * 0.38)}px ui-monospace, Consolas, monospace`;
      g.fillText('!', s.x + T / 2, s.y + T * 0.6);
    }

    // Light glow (flickers a little).
    g.globalCompositeOperation = 'lighter';
    for (const l of d.lights) {
      // A carried light moves with its bearer's gliding token.
      const off = glideAt.get(`${l.x},${l.y}`) ?? { dx: 0, dy: 0 };
      const s = this.tileToScreen(l.x + 0.5 + off.dx, l.y + 0.5 + off.dy);
      const r = Math.max(1.2, l.bright + 1) * T * (0.95 + 0.05 * Math.sin(time / 90 + l.x * 7 + l.y * 3));
      const grad = g.createRadialGradient(s.x, s.y, 0, s.x, s.y, r);
      grad.addColorStop(0, 'rgba(255,150,60,0.16)');
      grad.addColorStop(1, 'rgba(255,150,60,0)');
      g.fillStyle = grad;
      g.fillRect(s.x - r, s.y - r, r * 2, r * 2);
    }
    g.globalCompositeOperation = 'source-over';

    for (const z of d.darkness) {
      const s = this.tileToScreen(z.x + 0.5, z.y + 0.5);
      g.fillStyle = 'rgba(20,0,35,0.55)';
      g.beginPath();
      g.arc(s.x, s.y, (z.radius + 0.5) * T, 0, Math.PI * 2);
      g.fill();
    }

    const glyph = (x: number, y: number, ch: string, color: string, alpha = 1, scale = 0.72) => {
      const s = this.tileToScreen(x, y);
      g.globalAlpha = alpha;
      g.fillStyle = color;
      g.font = `bold ${Math.round(T * scale)}px ui-monospace, Consolas, monospace`;
      g.fillText(ch, s.x + T / 2, s.y + T / 2 + 1);
      g.globalAlpha = 1;
    };

    for (const f of d.features) {
      const v = visAt(f.x, f.y);
      glyph(f.x, f.y, f.glyph, f.color, v === '1' || v === '2' ? 1 : 0.4);
    }

    // Items: one glyph per square (the last one dropped) and a count for a pile. On a structure's
    // square (a campfire, the stairs) they sit in the corner so both stay visible.
    const piles = new Map<number, typeof d.items>();
    for (const it of d.items) {
      const k = it.y * d.width + it.x;
      piles.set(k, [...(piles.get(k) ?? []), it]);
    }
    const structures = new Set(d.features.map((f) => f.y * d.width + f.x));
    for (const [k, pile] of piles) {
      const top = pile[pile.length - 1];
      const s = this.tileToScreen(top.x, top.y);
      if (pile.some((it) => it.lit)) {
        g.fillStyle = 'rgba(255,170,60,0.35)';
        g.beginPath();
        g.arc(s.x + T / 2, s.y + T / 2, T * 0.42, 0, Math.PI * 2);
        g.fill();
      }
      let bx = s.x + T * 0.78, by = s.y + T * 0.22;
      if (structures.has(k)) {
        const cx = s.x + T * 0.74, cy = s.y + T * 0.74, r = T * 0.22;
        g.fillStyle = 'rgba(14,12,10,0.88)';
        g.strokeStyle = 'rgba(255,214,120,0.75)';
        g.lineWidth = 1;
        g.beginPath();
        g.arc(cx, cy, r, 0, Math.PI * 2);
        g.fill();
        g.stroke();
        g.fillStyle = top.color;
        g.font = `bold ${Math.round(T * 0.32)}px ui-monospace, Consolas, monospace`;
        g.fillText(top.glyph, cx, cy + 1);
        bx = cx + r * 0.75;
        by = cy - r * 0.8;
      } else {
        glyph(top.x, top.y, top.glyph, top.color, 1, 0.62);
      }
      if (pile.length > 1) {
        const r = Math.max(6, T * 0.15);
        g.fillStyle = '#e0a040';
        g.beginPath();
        g.arc(bx, by, r, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = '#1a140c';
        g.font = `bold ${Math.round(r * 1.3)}px ui-sans-serif, system-ui, sans-serif`;
        g.fillText(String(pile.length), bx, by + 0.5);
      }
    }

    // Where each token is drawn this frame (attackers lunge off their square).
    const drawnAt = new Map<string, { x: number; y: number }>();
    for (const c of d.creatures) {
      const off = fx?.offset(c.id, time) ?? { x: 0, y: 0 };
      const p = shown.get(c.id)!;
      drawnAt.set(c.id, { x: p.x + off.x, y: p.y + off.y });
    }
    // Living creatures on top of the fallen.
    const order = [...d.creatures].sort((a, b) => Number(b.health === 'dead') - Number(a.health === 'dead'));
    for (const c of order) this.drawCreature(c, overlay, time, drawnAt.get(c.id)!, fx?.flash(c.id, time) ?? 0);

    // Thought bubbles: "?" for someone who will talk, animated dots while they (or their agent) think.
    for (const e of d.encounters) {
      if (e.resolved && !e.thinking) continue;
      const s = this.tileToScreen(e.x, e.y);
      this.drawThought(s.x + T * 0.78, s.y - T * 0.05, T * 0.22, e.thinking, time);
    }
    for (const c of d.creatures) {
      if (!c.thinking || d.encounters.some((e) => e.x === c.x && e.y === c.y)) continue;
      const at = drawnAt.get(c.id)!;
      const s = this.tileToScreen(at.x, at.y);
      const size = this.tile * (c.size ?? 1);
      this.drawThought(s.x + size * 0.78, s.y - this.tile * 0.05, this.tile * 0.22, true, time);
    }
    // Faction quests: a gold "!" over a giver with an errand for you, a gold "?" over whoever would take a keepsake back.
    for (const c of d.creatures) {
      if ((c.quest !== 'offer' && c.quest !== 'turn-in') || c.thinking || c.health === 'dead') continue;
      const at = drawnAt.get(c.id)!;
      const s = this.tileToScreen(at.x, at.y);
      this.drawQuestMark(s.x + this.tile * (c.size ?? 1) * 0.78, s.y - this.tile * 0.05, this.tile * 0.22, c.quest === 'offer' ? '!' : '?');
    }
    // What was just said, over the speaker's token.
    for (const b of overlay.bubbles) {
      const c = d.creatures.find((x) => x.id === b.id);
      const at = c ? drawnAt.get(c.id) : undefined;
      if (!c || !at || c.detection === 'hidden') continue;
      const s = this.tileToScreen(at.x, at.y);
      this.drawSpeech(b.text, s.x + (this.tile * (c.size ?? 1)) / 2, s.y, b.age, b.ms, c.isPc);
    }

    if (overlay.hover) {
      const s = this.tileToScreen(overlay.hover.x, overlay.hover.y);
      g.strokeStyle = 'rgba(255,255,255,0.6)';
      g.lineWidth = 1;
      g.strokeRect(s.x + 0.5, s.y + 0.5, T - 1, T - 1);
    }

    // Aim lines and numbers find a big creature's middle (they add half a square to this).
    const sizes = new Map(d.creatures.map((c) => [c.id, c.size ?? 1]));
    if (fx) {
      this.drawEffects(fx, time, (id) => {
        const p = drawnAt.get(id);
        const half = ((sizes.get(id) ?? 1) - 1) / 2;
        return p ? { x: p.x + half, y: p.y + half } : null;
      });
    }
  }

  /** A small thought bubble: "?" (will talk) or animated dots (thinking). */
  private drawThought(bx: number, by: number, r: number, thinking: boolean, time: number): void {
    const g = this.g;
    g.fillStyle = 'rgba(20,16,12,0.9)';
    g.strokeStyle = thinking ? '#9fc5ff' : '#e0b84a';
    g.lineWidth = 1.5;
    g.beginPath();
    g.ellipse(bx, by, r * 1.3, r, 0, 0, Math.PI * 2);
    g.fill();
    g.stroke();
    g.fillStyle = thinking ? '#9fc5ff' : '#e0b84a';
    if (thinking) {
      for (let k = 0; k < 3; k++) {
        const on = Math.floor(time / 300) % 3 >= k;
        g.globalAlpha = on ? 1 : 0.3;
        g.beginPath();
        g.arc(bx + (k - 1) * r * 0.55, by, r * 0.14, 0, Math.PI * 2);
        g.fill();
      }
      g.globalAlpha = 1;
    } else {
      g.font = `bold ${Math.round(r * 1.4)}px ui-sans-serif, system-ui, sans-serif`;
      g.fillText('?', bx, by + 1);
    }
  }

  /** A faction quest's mark over a token: a solid gold bubble with "!" (an errand for you) or "?" (it would take a keepsake back). */
  private drawQuestMark(bx: number, by: number, r: number, mark: '!' | '?'): void {
    const g = this.g;
    g.fillStyle = '#f5c542';
    g.strokeStyle = '#3a2a00';
    g.lineWidth = 1.5;
    g.beginPath();
    g.ellipse(bx, by, r * 1.1, r, 0, 0, Math.PI * 2);
    g.fill();
    g.stroke();
    g.fillStyle = '#2a1e00';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = `bold ${Math.round(r * 1.5)}px ui-sans-serif, system-ui, sans-serif`;
    g.fillText(mark, bx, by + 1);
  }

  /** A speech bubble with all the words just said, above a token, fading in and out. */
  private drawSpeech(text: string, cx: number, top: number, age: number, ms: number, pc: boolean): void {
    const g = this.g;
    const alpha = age < 150 ? age / 150 : age > ms - 800 ? Math.max(0, (ms - age) / 800) : 1;
    if (alpha <= 0) return;
    const font = 13;
    g.font = `${font}px ui-sans-serif, system-ui, sans-serif`;
    // Long speeches get a wider bubble, so they wrap into fewer lines (never wider than the map).
    const wide = text.length > 120 ? 360 : 280;
    const maxWidth = Math.max(150, Math.min(wide, this.tile * (text.length > 120 ? 7 : 5), this.cssWidth - 24));
    const lines: string[] = [];
    let line = '';
    for (const word of text.split(/\s+/)) {
      const next = line ? `${line} ${word}` : word;
      if (g.measureText(next).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else line = next;
    }
    if (line) lines.push(line);
    const w = Math.min(maxWidth, Math.max(...lines.map((l) => g.measureText(l).width))) + 16;
    const h = lines.length * (font + 3) + 10;
    const x = Math.max(4, Math.min(this.cssWidth - w - 4, cx - w / 2));
    const y = Math.max(4, top - h - 10);
    g.globalAlpha = alpha;
    g.fillStyle = 'rgba(20,16,12,0.93)';
    g.strokeStyle = pc ? '#9fc5ff' : '#e0b84a';
    g.lineWidth = 1.5;
    g.beginPath();
    g.roundRect(x, y, w, h, 8);
    g.fill();
    g.stroke();
    // The tail, pointing at the speaker.
    const tx = Math.max(x + 10, Math.min(x + w - 10, cx));
    g.beginPath();
    g.moveTo(tx - 6, y + h - 0.5);
    g.lineTo(tx, Math.min(top - 2, y + h + 8));
    g.lineTo(tx + 6, y + h - 0.5);
    g.closePath();
    g.fill();
    g.fillStyle = '#f1e6d0';
    g.textAlign = 'left';
    g.textBaseline = 'top';
    lines.forEach((l, i) => g.fillText(l, x + 8, y + 6 + i * (font + 3)));
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.globalAlpha = 1;
  }

  /** Aim lines, missiles and floating numbers, over everything else on the map. */
  private drawEffects(fx: Effects, time: number, where: (id: string) => { x: number; y: number } | null): void {
    const g = this.g;
    const T = this.tile;
    for (const a of fx.activeAims(time)) {
      const from = where(a.actorId), to = where(a.targetId);
      if (!from || !to) continue;
      const p = this.tileToScreen(from.x + 0.5, from.y + 0.5), q = this.tileToScreen(to.x + 0.5, to.y + 0.5);
      const len = Math.hypot(q.x - p.x, q.y - p.y);
      const fade = Math.min(1, a.age / 120);
      g.globalAlpha = 0.9 * fade;
      g.strokeStyle = a.color;
      if (len > T * 0.9) {
        const ux = (q.x - p.x) / len, uy = (q.y - p.y) / len;
        const sx = p.x + ux * T * 0.48, sy = p.y + uy * T * 0.48;
        const ex = q.x - ux * T * 0.62, ey = q.y - uy * T * 0.62;
        g.lineWidth = Math.max(1.5, T / 20);
        g.setLineDash([T * 0.18, T * 0.12]);
        g.lineDashOffset = -a.age / 25;
        g.beginPath();
        g.moveTo(sx, sy);
        g.lineTo(ex, ey);
        g.stroke();
        g.setLineDash([]);
        const h = T * 0.2;
        g.fillStyle = a.color;
        g.beginPath();
        g.moveTo(ex + ux * h * 0.6, ey + uy * h * 0.6);
        g.lineTo(ex - ux * h * 0.6 - uy * h * 0.55, ey - uy * h * 0.6 + ux * h * 0.55);
        g.lineTo(ex - ux * h * 0.6 + uy * h * 0.55, ey - uy * h * 0.6 - ux * h * 0.55);
        g.closePath();
        g.fill();
      }
      // The target's crosshair ring.
      const pulse = 0.5 + 0.5 * Math.sin(a.age / 90);
      g.lineWidth = 2;
      g.beginPath();
      g.arc(q.x, q.y, T * (0.54 + pulse * 0.05), 0, Math.PI * 2);
      g.stroke();
      for (let k = 0; k < 4; k++) {
        const ang = (k * Math.PI) / 2 + Math.PI / 4;
        g.beginPath();
        g.moveTo(q.x + Math.cos(ang) * T * 0.5, q.y + Math.sin(ang) * T * 0.5);
        g.lineTo(q.x + Math.cos(ang) * T * 0.66, q.y + Math.sin(ang) * T * 0.66);
        g.stroke();
      }
      g.globalAlpha = 1;
    }

    g.lineCap = 'round';
    for (const m of fx.activeMissiles(time)) {
      const head = this.tileToScreen(m.x, m.y), tail = this.tileToScreen(m.tx, m.ty);
      const grad = g.createLinearGradient(tail.x, tail.y, head.x, head.y);
      grad.addColorStop(0, 'rgba(255,255,255,0)');
      grad.addColorStop(1, m.color);
      g.strokeStyle = grad;
      g.lineWidth = Math.max(2, T / 13);
      g.beginPath();
      g.moveTo(tail.x, tail.y);
      g.lineTo(head.x, head.y);
      g.stroke();
      g.fillStyle = '#fff8e0';
      g.beginPath();
      g.arc(head.x, head.y, Math.max(1.5, T / 16), 0, Math.PI * 2);
      g.fill();
    }
    g.lineCap = 'butt';

    g.lineJoin = 'round';
    for (const f of fx.activeFloaters(time, where)) {
      const s = this.tileToScreen(f.x, f.y);
      const size = Math.round(Math.max(13, T * 0.46) * f.scale);
      g.globalAlpha = Math.max(0, f.alpha);
      g.font = `900 ${size}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
      g.lineWidth = Math.max(3, size / 5);
      g.strokeStyle = 'rgba(0,0,0,0.9)';
      g.strokeText(f.text, s.x, s.y);
      g.fillStyle = f.color;
      g.fillText(f.text, s.x, s.y);
    }
    g.globalAlpha = 1;
    g.lineJoin = 'miter';
  }

  private drawCreature(c: CreatureView, overlay: Overlay, time: number, at: { x: number; y: number }, flash: number): void {
    const g = this.g;
    // A Large or Huge creature's token fills its whole space.
    const T = this.tile * (c.size ?? 1);
    const s = this.tileToScreen(at.x, at.y);
    const cx = s.x + T / 2, cy = s.y + T / 2;
    const dead = c.health === 'dead';
    const down = c.health === 'down';
    // A party member some foes have lost through its Stealth: faded, in a dashed ring (health and markers stay).
    const sneaking = !!c.sneaking && c.sneaking.hidden + c.sneaking.undetected > 0;
    const fade = sneaking ? 0.55 : c.detection === 'concealed' ? 0.6 : 1;
    if (overlay.turnGlow === c.id) {
      // Your turn: a warm halo behind the token and a breathing ring around it.
      const pulse = 0.5 + 0.5 * Math.sin(time / 260);
      const halo = g.createRadialGradient(cx, cy, T * 0.4, cx, cy, T * (0.78 + pulse * 0.08));
      halo.addColorStop(0, `rgba(255,214,90,${(0.45 + pulse * 0.25).toFixed(3)})`);
      halo.addColorStop(1, 'rgba(255,214,90,0)');
      g.fillStyle = halo;
      g.beginPath();
      g.arc(cx, cy, T * 0.86, 0, Math.PI * 2);
      g.fill();
      g.strokeStyle = `rgba(255,230,120,${(0.65 + pulse * 0.35).toFixed(3)})`;
      g.lineWidth = 2.5;
      g.beginPath();
      g.arc(cx, cy, T * (0.56 + pulse * 0.05), 0, Math.PI * 2);
      g.stroke();
    }
    g.fillStyle = c.isPc ? 'rgba(10,20,30,0.85)' : 'rgba(30,8,8,0.85)';
    g.beginPath();
    g.arc(cx, cy, T * 0.44, 0, Math.PI * 2);
    g.fill();
    const token = dead || c.detection === 'hidden' ? null : tokenImage(c.token);
    if (token) {
      const r = T * 0.44;
      g.save();
      g.beginPath();
      g.arc(cx, cy, r, 0, Math.PI * 2);
      g.clip();
      g.globalAlpha = fade;
      g.drawImage(token, cx - r, cy - r, r * 2, r * 2);
      if (down) {
        g.fillStyle = 'rgba(0,0,0,0.55)';
        g.fillRect(cx - r, cy - r, r * 2, r * 2);
      }
      g.restore();
      g.globalAlpha = 1;
    }
    if (flash > 0) {
      g.fillStyle = `rgba(255,30,20,${(0.6 * flash).toFixed(3)})`;
      g.beginPath();
      g.arc(cx, cy, T * 0.44, 0, Math.PI * 2);
      g.fill();
      g.strokeStyle = `rgba(255,70,50,${flash.toFixed(3)})`;
      g.lineWidth = 2;
      g.beginPath();
      g.arc(cx, cy, T * (0.44 + (1 - flash) * 0.2), 0, Math.PI * 2);
      g.stroke();
      g.beginPath();
      g.arc(cx, cy, T * 0.44, 0, Math.PI * 2);
    }
    if (c.detection !== 'observed' || sneaking) g.setLineDash([3, 3]);
    g.strokeStyle = sneaking ? '#5fb3a8' : c.isYou ? '#ffffff' : c.isPc ? c.color : c.faction === 'neutral' ? '#e0b84a' : c.ally ? '#6fd0ff' : c.detection === 'hidden' ? '#666' : '#a33';
    g.lineWidth = c.isYou || c.faction === 'neutral' || c.ally ? 2 : 1;
    g.stroke();
    g.setLineDash([]);
    if (c.allegiance?.hue !== undefined && !dead) this.drawFactionMark(cx, cy, T, c.allegiance.hue, !!c.allegiance.known);
    if (overlay.currentIds.has(c.id)) {
      g.strokeStyle = '#ffe066';
      g.lineWidth = 2;
      g.beginPath();
      g.arc(cx, cy, T * 0.52, 0, Math.PI * 2);
      g.stroke();
    }
    if (overlay.talkTargets.has(c.id)) {
      // The one you're talking to: a slow, turning ring.
      const pulse = 0.5 + 0.5 * Math.sin(time / 400);
      g.strokeStyle = `rgba(150,205,255,${0.55 + pulse * 0.45})`;
      g.lineWidth = 2.5;
      g.setLineDash([6, 4]);
      g.lineDashOffset = -time / 60;
      g.beginPath();
      g.arc(cx, cy, T * 0.6, 0, Math.PI * 2);
      g.stroke();
      g.setLineDash([]);
      g.lineDashOffset = 0;
    }
    if (overlay.targets.has(c.id)) {
      const pulse = 0.5 + 0.5 * Math.sin(time / 150);
      g.strokeStyle = `rgba(255,90,60,${0.5 + pulse * 0.5})`;
      g.lineWidth = 2;
      g.beginPath();
      g.arc(cx, cy, T * 0.56, 0, Math.PI * 2);
      g.stroke();
    }
    if (!token) {
      g.globalAlpha = fade;
      g.fillStyle = dead || down ? '#777' : c.color;
      g.font = `bold ${Math.round(T * 0.66)}px ui-monospace, Consolas, monospace`;
      g.fillText(dead ? '%' : c.glyph, cx, cy + 1);
      g.globalAlpha = 1;
    }
    if (c.carriesLight) {
      g.fillStyle = '#ffb347';
      g.beginPath();
      g.arc(s.x + T * 0.82, s.y + T * 0.18, T * 0.1, 0, Math.PI * 2);
      g.fill();
    }
    if (c.autopilot && !dead) this.drawAutopilot(cx, s.y, T, time);
    if (c.boss && !dead) {
      // The floor's boss: a small gold crown over the top-left of its token.
      const x = s.x + T * 0.06, y = s.y + T * 0.06, w = T * 0.3, h = T * 0.2;
      g.fillStyle = '#f5c542';
      g.strokeStyle = '#3a2a00';
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(x, y + h);
      g.lineTo(x, y + h * 0.25);
      g.lineTo(x + w * 0.25, y + h * 0.6);
      g.lineTo(x + w * 0.5, y);
      g.lineTo(x + w * 0.75, y + h * 0.6);
      g.lineTo(x + w, y + h * 0.25);
      g.lineTo(x + w, y + h);
      g.closePath();
      g.fill();
      g.stroke();
    }
    if (c.quest === 'holder' && !dead) {
      // It carries a keepsake your errand wants: a small red diamond at the top left (beside a boss's crown).
      const r = T * 0.1, x = s.x + T * (c.boss ? 0.4 : 0.06), y = s.y + T * 0.06;
      g.fillStyle = '#e04a4a';
      g.strokeStyle = '#3a0a0a';
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(x + r, y);
      g.lineTo(x + 2 * r, y + r);
      g.lineTo(x + r, y + 2 * r);
      g.lineTo(x, y + r);
      g.closePath();
      g.fill();
      g.stroke();
    }
    if (c.detection !== 'hidden' && !dead) {
      const ratio = c.hp !== undefined && c.maxHp ? c.hp / c.maxHp : HEALTH_RATIO[c.health];
      g.fillStyle = '#300';
      g.fillRect(s.x + 3, s.y + T - 4, T - 6, 3);
      g.fillStyle = ratio > 0.5 ? '#6c3' : ratio > 0.25 ? '#db3' : '#d33';
      g.fillRect(s.x + 3, s.y + T - 4, (T - 6) * Math.max(0, Math.min(1, ratio)), 3);
      if (c.status.length) this.drawStatus(c, s.x, s.y, T);
    }
  }

  /**
   * Its faction's mark: a short arc in the faction's colour over the top of the token (between the status columns), on a dark
   * underlay so it reads on any floor. Dashed for a faction your character doesn't know yet (the colour still groups its members).
   */
  private drawFactionMark(cx: number, cy: number, T: number, hue: number, known: boolean): void {
    const g = this.g;
    const r = T * 0.5, from = Math.PI * 1.37, to = Math.PI * 1.63, w = Math.max(2.5, T * 0.09);
    g.lineCap = 'round';
    g.strokeStyle = 'rgba(0,0,0,0.75)';
    g.lineWidth = w + 2;
    g.beginPath();
    g.arc(cx, cy, r, from, to);
    g.stroke();
    if (!known) g.setLineDash([w * 1.2, w]);
    g.strokeStyle = factionColor(hue);
    g.lineWidth = w;
    g.beginPath();
    g.arc(cx, cy, r, from, to);
    g.stroke();
    g.setLineDash([]);
    g.lineCap = 'butt';
  }

  /**
   * A character the computer plays for its player (autopilot): a slowly turning dashed ring in cyan
   * and an "AUTO" tab over the top of the token, so everyone can tell it isn't being steered by hand.
   */
  private drawAutopilot(cx: number, top: number, T: number, time: number): void {
    const g = this.g;
    const cy = top + T / 2;
    g.strokeStyle = 'rgba(110,225,235,0.9)';
    g.lineWidth = 2;
    g.setLineDash([4, 4]);
    g.lineDashOffset = time / 80;
    g.beginPath();
    g.arc(cx, cy, T * 0.5, 0, Math.PI * 2);
    g.stroke();
    g.setLineDash([]);
    g.lineDashOffset = 0;
    const h = Math.max(9, T * 0.24), w = h * 2.6, x = cx - w / 2, y = top - h * 0.35;
    g.fillStyle = 'rgba(8,34,40,0.95)';
    g.strokeStyle = 'rgba(110,225,235,0.95)';
    g.lineWidth = 1;
    g.beginPath();
    g.roundRect(x, y, w, h, h / 2);
    g.fill();
    g.stroke();
    g.fillStyle = '#bff6fb';
    g.font = `800 ${Math.round(h * 0.7)}px ui-sans-serif, system-ui, sans-serif`;
    g.fillText('AUTO', cx, y + h / 2 + 1);
  }

  /**
   * Status markers in columns down the token's left edge, then its right (below the light dot),
   * with a value badge for valued conditions and "+N" when they don't all fit.
   */
  private drawStatus(c: CreatureView, sx: number, sy: number, T: number): void {
    const g = this.g;
    const marks = sortedStatus(c);
    const d = Math.max(13, Math.min(22, T * 0.34)), r = d / 2, step = d * 0.96;
    const slots: { x: number; y: number }[] = [];
    for (let y = sy + r + 1; y + r <= sy + T + 1; y += step) slots.push({ x: sx + r + 1, y });
    for (let y = sy + r + 1 + (c.carriesLight ? T * 0.28 : 0); y + r <= sy + T + 1; y += step) slots.push({ x: sx + T - r - 1, y });
    if (!slots.length) return;
    const overflow = marks.length > slots.length;
    const shown = overflow ? marks.slice(0, slots.length - 1) : marks;
    const badge = (p: { x: number; y: number }, ring: string) => {
      g.fillStyle = 'rgba(14,12,10,0.92)';
      g.beginPath();
      g.arc(p.x, p.y, r, 0, Math.PI * 2);
      g.fill();
      g.strokeStyle = ring;
      g.lineWidth = 1.5;
      g.stroke();
    };
    shown.forEach((m, i) => {
      const p = slots[i];
      const style = statusStyle(m);
      badge(p, style.color);
      g.fillStyle = style.color;
      g.font = `${Math.round(d * 0.68)}px ${EMOJI_FONT}`;
      g.fillText(style.icon, p.x, p.y + 1);
      if (m.value !== undefined) {
        const vx = p.x + r * 0.62, vy = p.y + r * 0.6;
        g.font = `900 ${Math.round(d * 0.55)}px ui-sans-serif, system-ui, sans-serif`;
        g.lineWidth = 2.5;
        g.lineJoin = 'round';
        g.strokeStyle = 'rgba(0,0,0,0.95)';
        g.strokeText(String(m.value), vx, vy);
        g.fillStyle = '#fff';
        g.fillText(String(m.value), vx, vy);
        g.lineJoin = 'miter';
      }
    });
    if (overflow) {
      const p = slots[slots.length - 1];
      badge(p, '#a7b0b8');
      g.fillStyle = '#e8e8e8';
      g.font = `bold ${Math.round(d * 0.5)}px ui-sans-serif, system-ui, sans-serif`;
      g.fillText(`+${marks.length - shown.length}`, p.x, p.y + 1);
    }
  }
}
